import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../../framework/BaseWebComponentController';
import { IntegrityMonitorEventsWebComponent } from '../component/IntegrityMonitorEventsWebComponent';
import { IntegrityMonitorEventsRepository } from '../repository/IntegrityMonitorEventsRepository';
import { IntegrityMonitorEventsView } from '../view/IntegrityMonitorEventsView';
import { templateIntegrityMonitorEvents } from '../view/template';
import { GridEvents } from '../../../../grid/types/common';
import { IntegrityMonitorDetailsPanelWebComponent } from '../../integrityMonitorEventsDetailsPanel';
import {
  getIntegrityMonitorCategory,
  getIntegrityMonitorChangeValues,
  GridActions,
  IntegrityMonitorEventsGridItem,
  IntegrityMonitorEventsWebComponentSchema,
} from '../types';
import { getIntegrityMonitorTypeValues } from '../../../common/utils/types';
import { SelectEvents } from '../../../../ui/gzSelect/types';
import { showErrorToast, showSuccessToast } from '@nirvana/shared/common';
import { IntegrityMonitorEventsPanelConfig } from '../../integrityMonitorEventsDetailsPanel/types';
import { EnumListEvents } from '../../../../ui/gzEnumList/types';

export class IntegrityMonitorEventsController extends BaseWebComponentController {
  public component: IntegrityMonitorEventsWebComponent;
  public view: IntegrityMonitorEventsView;
  public viewConstructor: typeof IntegrityMonitorEventsView;
  public dataRepository: IntegrityMonitorEventsRepository;
  public dataRepositoryConstructor: typeof IntegrityMonitorEventsRepository;

  private selectedItemsGridData: Array<IntegrityMonitorEventsGridItem>;

  constructor(component: IntegrityMonitorEventsWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: IntegrityMonitorEventsView,
      viewTemplate: templateIntegrityMonitorEvents,
      dataRepositoryConstructor: IntegrityMonitorEventsRepository,
    });
  }

  async getDataForView(): Promise<IntegrityMonitorEventsWebComponentSchema> {
    return await this.dataRepository.getDataForView();
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$changeCategorySelect,
        event: SelectEvents.SELECTION_CHANGED,
        callback: this.onSelectionChanged,
      },
      {
        element: this.view.dom.$gridView,
        event: GridEvents.ROW_CLICKED,
        callback: this.onDetailsClick,
      },
      {
        element: this.view.dom.$companyFilter,
        event: EnumListEvents.SELECTION_CHANGE,
        callback: this.onCompanySelectionChange,
      },
      {
        element: this.view.dom.$gridView,
        event: GridEvents.SELECT_ROWS,
        callback: this.onEventSelected,
      },
    ];
  }

  public closeSideMenu(): void {
    this.view.dom.$sideMenu.closeSideMenu();
  }

  private onDetailsClick(event: WebComponentEvent) {
    if (event.detail.rowDetails) {
      const panel = this.buildEventDetailsPanel(event.detail.rowDetails);
      this.showEventDetailsPanel(panel);
    }
  }

  private async onSelectionChanged(event: WebComponentEvent) {
    await this.updateCategory(event.detail.selected);
  }

  private onEventSelected(event: WebComponentEvent) {
    this.selectedItemsGridData = event.detail.selectionStoreData;
    this.handleCategoryOptionEnabling();
  }

  private handleCategoryOptionEnabling() {
    const shouldDisable = this.selectedItemsGridData.length > 0;

    this.view.setCategoryOptionStatus(GridActions.APPROVE_EVENT, shouldDisable);
    this.view.setCategoryOptionStatus(GridActions.UNAPPROVE_EVENT, shouldDisable);
  }

  private buildEventDetailsPanel(details: any): IntegrityMonitorDetailsPanelWebComponent {
    const panel = new IntegrityMonitorDetailsPanelWebComponent();
    panel.injectDependencies(this.dependencies);

    const panelConfig: IntegrityMonitorEventsPanelConfig = {
      event_time: details.event_time,
      endpoint_id: details.endpoint_id,
      endpoint_name: details.endpoint_name,
      hostname: details.hostname,
      rule_name: details.rule_name,
      change_type:
        getIntegrityMonitorChangeValues(this.dependencies.translateFn).find(
          element => element.value === details.change_type
        )?.label ?? this.dependencies.translateFn('Unknown'),
      severity: details.severity,
      entity_type:
        getIntegrityMonitorTypeValues(this.dependencies.translateFn).find(
          element => element.value === details.entity_type
        )?.label ?? this.dependencies.translateFn('Unknown'),
      mitre_ids: details.mitre_ids ?? [],
      key: details.key,
      user: details.user,
      changes: details.changes ?? [],
      category:
        getIntegrityMonitorCategory(this.dependencies.translateFn).find(element => element.value === details.category)
          ?.label ?? this.dependencies.translateFn('Unknown'),
      actions_result: details.actions_result ?? [],
    };

    panel.setPanelConfig(panelConfig);
    return panel;
  }

  private showEventDetailsPanel(panel: IntegrityMonitorDetailsPanelWebComponent) {
    this.view.dom.$sideMenu.closeOnClickOutside = true;
    this.view.dom.$sideMenu.isOpen = true;
    this.view.dom.$sideMenu.setMenuContent(panel);
    this.view.dom.$sideMenu.openSideMenu();
  }

  private async updateCategory(newCategory: string) {
    const records = this.selectedItemsGridData.map(eventData => ({
      _id: eventData._id,
      category: eventData.category,
      key: eventData.key,
      companyId: eventData.company_id,
    }));

    try {
      await this.dataRepository.updateCategory(records, newCategory);

      showSuccessToast(this.dependencies.translateFn('Category update initiated'));
    } catch (error) {
      if (error?.exceptionType === 'Exceptions\\AuthenticationSecurityException') {
        return;
      }

      showErrorToast(error);
    }
  }

  private async onCompanySelectionChange(event: WebComponentEvent) {
    this.view.dom.$gridView.extraParams = {
      companyIds: event.detail.selectedValues,
    };

    await this.view.dom.$gridView.controller.loadFirstPage();
  }
}
